import { useState, useMemo } from 'react';
import type { AdminUpgrade, AdminUpgradeInput } from '../../api/admin';
import { X, Save, AlertCircle } from 'lucide-react';

interface UpgradeEditModalProps {
  upgrade: AdminUpgrade;
  allUpgrades: AdminUpgrade[];
  onSave: (id: string, data: AdminUpgradeInput) => void;
  onClose: () => void;
  isSaving: boolean;
}

const CATEGORIES = ['geology', 'atmosphere', 'water', 'life', 'plant', 'civilisation', 'technology'];
const REPEATABILITIES = ['once', 'limited', 'unlimited'];

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export default function UpgradeEditModal({ upgrade, allUpgrades, onSave, onClose, isSaving }: UpgradeEditModalProps) {
  const [name, setName] = useState(upgrade.name);
  const [description, setDescription] = useState(upgrade.description || '');
  const [flavourText, setFlavourText] = useState(upgrade.flavour_text || '');
  const [category, setCategory] = useState(upgrade.category);
  const [stageRequirement, setStageRequirement] = useState(upgrade.stage_requirement);
  const [cost, setCost] = useState(upgrade.cost);
  const [maxRepeats, setMaxRepeats] = useState(upgrade.max_repeats);
  const [constructionDays, setConstructionDays] = useState(upgrade.construction_days);
  const [upkeepCost, setUpkeepCost] = useState(upgrade.upkeep_cost);
  const [repeatability, setRepeatability] = useState(upgrade.repeatability);
  const [active, setActive] = useState(upgrade.active);
  const [conflictGroup, setConflictGroup] = useState(upgrade.conflict_group || '');
  const [effects, setEffects] = useState(safeJsonStringify(upgrade.effects));
  const [dailyEffects, setDailyEffects] = useState(safeJsonStringify(upgrade.daily_effects));
  const [completionEffects, setCompletionEffects] = useState(safeJsonStringify(upgrade.completion_effects));
  const [placementRules, setPlacementRules] = useState(safeJsonStringify(upgrade.placement_rules));
  const [unlockRules, setUnlockRules] = useState(safeJsonStringify(upgrade.unlock_rules));
  const [selectedPrereqs, setSelectedPrereqs] = useState<Set<string>>(new Set(upgrade.prerequisites.map((p) => p.id)));
  const [synergyWith, setSynergyWith] = useState(upgrade.synergy_with.join(', '));
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});

  const otherUpgrades = useMemo(() => allUpgrades.filter((u) => u.upgrade_definition_id !== upgrade.upgrade_definition_id), [allUpgrades, upgrade]);

  const validateJson = () => {
    const errors: Record<string, string> = {};
    const fields = [
      { key: 'effects', value: effects },
      { key: 'dailyEffects', value: dailyEffects },
      { key: 'completionEffects', value: completionEffects },
      { key: 'placementRules', value: placementRules },
      { key: 'unlockRules', value: unlockRules },
    ];
    for (const { key, value } of fields) {
      const parsed = safeJsonParse(value);
      if (parsed === null && value.trim() !== '{}' && value.trim() !== '') {
        errors[key] = 'Invalid JSON';
      }
    }
    setJsonErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateJson()) return;

    const data: AdminUpgradeInput = {
      name: name.trim(),
      description: description.trim() || undefined,
      flavour_text: flavourText.trim() || undefined,
      category,
      stage_requirement: stageRequirement,
      cost,
      max_repeats: maxRepeats,
      construction_days: constructionDays,
      upkeep_cost: upkeepCost,
      repeatability,
      active,
      conflict_group: conflictGroup.trim() || null,
      effects: safeJsonParse(effects) as Record<string, unknown>,
      daily_effects: safeJsonParse(dailyEffects) as Record<string, unknown>,
      completion_effects: safeJsonParse(completionEffects) as Record<string, unknown>,
      placement_rules: safeJsonParse(placementRules) as Record<string, unknown>,
      unlock_rules: safeJsonParse(unlockRules) as Record<string, unknown>,
      prerequisites: Array.from(selectedPrereqs),
      synergy_with: synergyWith.split(',').map((s) => s.trim()).filter(Boolean),
    };

    onSave(upgrade.upgrade_definition_id, data);
  };

  const togglePrereq = (id: string) => {
    setSelectedPrereqs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const inputClass = 'w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/30';
  const labelClass = 'text-xs font-medium text-slate-400';
  const sectionClass = 'space-y-3';
  const sectionTitleClass = 'text-xs font-semibold uppercase tracking-wider text-amber-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-white">Edit Development</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${active ? 'bg-emerald-950/70 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
              {active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Basic */}
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Basic</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelClass}>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Stage Requirement</label>
                <input type="number" min={0} max={10} value={stageRequirement} onChange={(e) => setStageRequirement(Number(e.target.value))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Cost (credits)</label>
                <input type="number" min={0} value={cost} onChange={(e) => setCost(Number(e.target.value))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Repeatability</label>
                <select value={repeatability} onChange={(e) => setRepeatability(e.target.value)} className={inputClass}>
                  {REPEATABILITIES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Max Repeats</label>
                <input type="number" min={1} value={maxRepeats} onChange={(e) => setMaxRepeats(Number(e.target.value))} className={inputClass} />
              </div>
              <div className="flex items-center gap-3 pt-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30" />
                  <span className="text-sm text-slate-300">Active</span>
                </label>
              </div>
            </div>
          </div>

          {/* Effects */}
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Effects (JSON)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Base Effects</label>
                <textarea value={effects} onChange={(e) => setEffects(e.target.value)} rows={4} className={`${inputClass} font-mono text-xs`} />
                {jsonErrors.effects && <div className="text-[10px] text-red-400 mt-1">{jsonErrors.effects}</div>}
              </div>
              <div>
                <label className={labelClass}>Daily Effects</label>
                <textarea value={dailyEffects} onChange={(e) => setDailyEffects(e.target.value)} rows={4} className={`${inputClass} font-mono text-xs`} />
                {jsonErrors.dailyEffects && <div className="text-[10px] text-red-400 mt-1">{jsonErrors.dailyEffects}</div>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Completion Effects</label>
                <textarea value={completionEffects} onChange={(e) => setCompletionEffects(e.target.value)} rows={3} className={`${inputClass} font-mono text-xs`} />
                {jsonErrors.completionEffects && <div className="text-[10px] text-red-400 mt-1">{jsonErrors.completionEffects}</div>}
              </div>
            </div>
          </div>

          {/* Placement & Unlock */}
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Placement & Unlock (JSON)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Placement Rules</label>
                <textarea value={placementRules} onChange={(e) => setPlacementRules(e.target.value)} rows={4} className={`${inputClass} font-mono text-xs`} />
                {jsonErrors.placementRules && <div className="text-[10px] text-red-400 mt-1">{jsonErrors.placementRules}</div>}
              </div>
              <div>
                <label className={labelClass}>Unlock Rules</label>
                <textarea value={unlockRules} onChange={(e) => setUnlockRules(e.target.value)} rows={4} className={`${inputClass} font-mono text-xs`} />
                {jsonErrors.unlockRules && <div className="text-[10px] text-red-400 mt-1">{jsonErrors.unlockRules}</div>}
              </div>
            </div>
          </div>

          {/* Prerequisites */}
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Prerequisites</div>
            <div className="max-h-48 overflow-y-auto border border-slate-800 rounded-lg p-2 space-y-1">
              {otherUpgrades.length === 0 ? (
                <div className="text-xs text-slate-500">No other upgrades available.</div>
              ) : (
                otherUpgrades.map((u) => (
                  <label key={u.upgrade_definition_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPrereqs.has(u.upgrade_definition_id)}
                      onChange={() => togglePrereq(u.upgrade_definition_id)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30"
                    />
                    <span className="text-xs text-slate-300">{u.name}</span>
                    <span className="text-[10px] text-slate-500 ml-auto">Stage {u.stage_requirement}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Advanced */}
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Advanced</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Construction Days</label>
                <input type="number" min={1} value={constructionDays} onChange={(e) => setConstructionDays(Number(e.target.value))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Upkeep Cost (cr/day)</label>
                <input type="number" min={0} value={upkeepCost} onChange={(e) => setUpkeepCost(Number(e.target.value))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Conflict Group</label>
                <input value={conflictGroup} onChange={(e) => setConflictGroup(e.target.value)} placeholder="e.g. energy_source" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Synergy With (comma-separated)</label>
                <input value={synergyWith} onChange={(e) => setSynergyWith(e.target.value)} placeholder="e.g. geology, water" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Flavour Text</label>
                <textarea value={flavourText} onChange={(e) => setFlavourText(e.target.value)} rows={2} className={inputClass} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800 shrink-0">
          {Object.keys(jsonErrors).length > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-red-400 mr-auto">
              <AlertCircle className="w-3 h-3" />
              Fix JSON errors before saving
            </div>
          )}
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 rounded-lg transition-colors"
          >
            {isSaving ? (
              <>
                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
